import { HSFormState } from './interfaces';
import {
  Code,
  NoteContent,
  HS_FORM_FIELD_BP,
  HS_FORM_FIELD_CHIEF_COMPLAINT,
  HS_FORM_FIELD_DIAGNOSIS,
  HS_FORM_FIELD_HR,
  HS_FORM_FIELD_RR,
  HS_FORM_FIELD_SI,
  HS_FORM_FIELD_SPO2,
  HS_FORM_FIELD_TEMP,
  TemplateSelection,
} from '@ax/constants';

export const fromOrganizeSelectionsToHSFormValues = (
  selections: Record<string, string>[] = [],
  state?: HSFormState,
): HSFormState => {
  const hsRR =
    selections.find((selection) => selection.selection.endsWith(HS_FORM_FIELD_RR))?.Data ?? '';
  const hsBP =
    selections.find((selection) => selection.selection.endsWith(HS_FORM_FIELD_BP))?.Data ?? '';
  const hsHR =
    selections.find((selection) => selection.selection.endsWith(HS_FORM_FIELD_HR))?.Data ?? '';
  const hsTemp =
    selections.find((selection) => selection.selection.endsWith(HS_FORM_FIELD_TEMP))?.Data ?? '';
  const hsSPO2 =
    selections.find((selection) => selection.selection.endsWith(HS_FORM_FIELD_SPO2))?.Data ?? '';
  const hsDiagnosis =
    selections.find((selection) => selection.selection.endsWith(HS_FORM_FIELD_DIAGNOSIS))?.Data ??
    '';
  const hsSI =
    selections.find((selection) => selection.selection.endsWith(HS_FORM_FIELD_SI))?.Data ?? '';
  const hsChiefComplaint =
    selections.find((selection) => selection.selection.endsWith(HS_FORM_FIELD_CHIEF_COMPLAINT))
      ?.Data ?? '';

  // determine if there any selections present for any of the fields
  const isDisabled = !selections.some((selection) =>
    [
      HS_FORM_FIELD_RR,
      HS_FORM_FIELD_BP,
      HS_FORM_FIELD_HR,
      HS_FORM_FIELD_TEMP,
      HS_FORM_FIELD_SPO2,
      HS_FORM_FIELD_DIAGNOSIS,
      HS_FORM_FIELD_SI,
      HS_FORM_FIELD_CHIEF_COMPLAINT,
    ].some((field) => selection.selection.endsWith(field)),
  );

  return {
    ...(state ?? {}),
    isDisabled,
    bp: {
      value: hsBP,
      identifier: HS_FORM_FIELD_BP,
      isLoading: false,
      error: !hsBP,
    },
    hr: {
      value: hsHR,
      identifier: HS_FORM_FIELD_HR,
      isLoading: false,
      error: !hsHR,
    },
    rr: {
      value: hsRR,
      identifier: HS_FORM_FIELD_RR,
      isLoading: false,
      error: !hsRR,
    },
    temp: {
      value: hsTemp,
      identifier: HS_FORM_FIELD_TEMP,
      isLoading: false,
      error: !hsTemp,
    },
    spo2: {
      value: hsSPO2,
      identifier: HS_FORM_FIELD_SPO2,
      isLoading: false,
      error: !hsSPO2,
    },
    diagnosis: {
      value: hsDiagnosis,
      identifier: HS_FORM_FIELD_DIAGNOSIS,
      isLoading: false,
      error: !hsDiagnosis,
    },
    secondaryImpression: {
      value: hsSI,
      identifier: HS_FORM_FIELD_SI,
      isLoading: false,
      error: !hsSI,
    },
    chiefComplaint: {
      value: hsChiefComplaint,
      identifier: HS_FORM_FIELD_CHIEF_COMPLAINT,
      isLoading: false,
      error: !hsChiefComplaint,
    },
  };
};

export function fromNoteContentToTemplateSelections(noteContentData: any) {
  if (!noteContentData?.getNoteContent) {
    return [];
  }

  const templatesMap = new Map<string, TemplateSelection>();

  noteContentData?.getNoteContent.forEach((item: NoteContent) => {
    if (!item.templateSelection) return;

    const { ComplaintId, ComplaintName, codes } = item.templateSelection;
    const section = item.Section;
    const key = `${section}-${ComplaintId}`; // Unique key based on section and ComplaintId

    if (!templatesMap.has(key)) {
      // If the key doesn't exist in the map, create a new entry
      templatesMap.set(key, {
        complaintId: ComplaintId,
        complaintName: ComplaintName,
        section: section,
        codes: codes.map((code: Code) => ({ ...code })).sort((a: Code, b: Code) => a.rank - b.rank),
      });

      if (
        templatesMap.get(key).codes.length > 0 &&
        !templatesMap.get(key).codes.find((code: Code) => code.code === 'NO-ICD-10-CODE')
      ) {
        templatesMap.get(key).codes.push({
          actionSource: 'User',
          code: 'NO-ICD-10-CODE',
          codeSystem: 'http://augmedix.com/ref/fhir/codesystems/internal',
          display: 'No ICD-10 Code',
          rank: templatesMap.get(key).codes[templatesMap.get(key).codes.length - 1].rank + 1,
          status: 'GENERATED',
          referenceSelection: codes[0].referenceSelection,
        });
      }
    }
  });

  return Array.from(templatesMap.values() || []);
}

export function getRandomString(length: number): string {
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  const charactersLength = characters.length;
  for (let i = 0; i < length; i++) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
  }
  return result;
}
